import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Megaphone, Search, Mail, Palette, TrendingUp, Share2, PenTool, Globe, Star, Shield, Zap, Brain, Video, Phone, MessageSquare, FileText, ArrowRight, CheckCircle2, Clock, LineChart, Database, RefreshCw, Layers, Target } from 'lucide-react';
export function AutopilotMarketing() {
  // Animation variants
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <main className="min-h-screen bg-white text-gray-900 font-inter selection:bg-purple/30 selection:text-white pt-20" data-id="element-1012">
      {/* 1. HERO SECTION */}
      <section className="relative pt-24 pb-32 px-6 bg-hero-gradient overflow-hidden min-h-[85vh] flex items-center" data-id="element-1013">
        {/* Ambient Glows */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1014" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1015" />

        {/* Floating Icons Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1016">
          {[{
          Icon: Megaphone,
          top: '20%',
          left: '10%',
          delay: 0
        }, {
          Icon: Search,
          top: '15%',
          left: '80%',
          delay: 1
        }, {
          Icon: Mail,
          top: '70%',
          left: '15%',
          delay: 2
        }, {
          Icon: Palette,
          top: '60%',
          left: '85%',
          delay: 0.5
        }, {
          Icon: TrendingUp,
          top: '40%',
          left: '90%',
          delay: 1.5
        }, {
          Icon: Share2,
          top: '80%',
          left: '50%',
          delay: 2.5
        }].map((item, i) => <motion.div key={i} className="absolute text-white/10" style={{
          top: item.top,
          left: item.left
        }} animate={{
          y: [0, -30, 0],
          rotate: [0, 15, -15, 0],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          delay: item.delay,
          ease: 'easeInOut'
        }} data-id="element-1017">
              <item.Icon size={64} data-id="element-1018" />
            </motion.div>)}
        </div>

        <div className="inner-container relative z-10 text-center max-w-5xl mx-auto" data-id="element-1019">
          <motion.div initial="hidden" animate="show" variants={staggerContainer} data-id="element-1020">
            <motion.span variants={fadeUp} className="eyebrow mx-auto" data-id="element-1021">
              Self-Running Marketing
            </motion.span>
            <motion.h1 variants={fadeUp} className="font-poppins font-extrabold text-5xl md:text-7xl leading-[1.1] mb-6 text-white" data-id="element-1022">
              Your Entire Marketing Department. <br data-id="element-1023" />
              <span className="text-gradient" data-id="element-1024">Running Itself.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-xl md:text-2xl text-steel mb-12 leading-relaxed max-w-3xl mx-auto" data-id="element-1025">
              Replace expensive agencies, unreliable freelancers, and scattered
              tools with one AI intelligence that knows your brand inside and
              out.
            </motion.p>

            {/* Stats Bar */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-4xl mx-auto bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm shadow-2xl" data-id="element-1026">
              <div data-id="element-1027">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-1028">
                  8
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1029">
                  Disciplines
                </div>
              </div>
              <div data-id="element-1030">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-1031">
                  24/7
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1032">
                  Execution
                </div>
              </div>
              <div data-id="element-1033">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-1034">
                  1
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1035">
                  Brand Voice
                </div>
              </div>
              <div data-id="element-1036">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-1037">
                  Zero
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1038">
                  Freelancers
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 2. THE PERSONA ENGINE SECTION */}
      <section className="py-24 px-6 bg-white relative overflow-hidden" data-id="element-1039">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1040">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1041">
            <span className="text-purple text-sm font-poppins font-bold uppercase tracking-widest block mb-4" data-id="element-1042">
              The Persona Engine
            </span>
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight text-gray-900 mb-6" data-id="element-1043">
              It Starts With Who You Are.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1044">
              Great marketing isn't just about posting—it's about capturing the
              exact essence, tone, and authority of your company. We build a
              deep, self-evolving brand persona by tapping into the data you
              already have.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-1045">
            {/* Visual Diagram */}
            <div className="lg:col-span-7 relative" data-id="element-1046">
              <div className="relative w-full aspect-square max-w-lg mx-auto" data-id="element-1047">
                {/* Center Brain */}
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-dark-bg rounded-full border-4 border-purple shadow-[0_0_40px_rgba(124,58,237,0.3)] flex items-center justify-center z-20" animate={{
                boxShadow: ['0 0 40px rgba(124,58,237,0.3)', '0 0 80px rgba(124,58,237,0.6)', '0 0 40px rgba(124,58,237,0.3)']
              }} transition={{
                duration: 4,
                repeat: Infinity
              }} data-id="element-1048">
                  <Brain size={48} className="text-white" data-id="element-1049" />
                </motion.div>

                {/* Connecting Lines (SVG) */}
                <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 400 400" data-id="element-1050">
                  <motion.circle cx="200" cy="200" r="120" fill="none" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="8 8" data-id="element-1051" />
                  {/* Animated data particles */}
                  <motion.circle cx="200" cy="80" r="4" fill="#7c3aed" animate={{
                  cy: [80, 160]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity
                }} data-id="element-1052" />
                  <motion.circle cx="80" cy="200" r="4" fill="#0785B7" animate={{
                  cx: [80, 160]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: 0.5
                }} data-id="element-1053" />
                  <motion.circle cx="320" cy="200" r="4" fill="#7c3aed" animate={{
                  cx: [320, 240]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: 1
                }} data-id="element-1054" />
                  <motion.circle cx="115" cy="315" r="4" fill="#0785B7" animate={{
                  cx: [115, 170],
                  cy: [315, 230]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: 0.2
                }} data-id="element-1055" />
                  <motion.circle cx="285" cy="315" r="4" fill="#7c3aed" animate={{
                  cx: [285, 230],
                  cy: [315, 230]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: 0.8
                }} data-id="element-1056" />
                </svg>

                {/* Nodes */}
                <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1057">
                  <Video size={24} className="text-teal" data-id="element-1058" />
                </div>
                <div className="absolute top-1/2 left-[10%] -translate-y-1/2 w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1059">
                  <Mail size={24} className="text-purple" data-id="element-1060" />
                </div>
                <div className="absolute top-1/2 right-[10%] -translate-y-1/2 w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1061">
                  <Phone size={24} className="text-teal" data-id="element-1062" />
                </div>
                <div className="absolute bottom-[15%] left-[20%] w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1063">
                  <MessageSquare size={24} className="text-purple" data-id="element-1064" />
                </div>
                <div className="absolute bottom-[15%] right-[20%] w-16 h-16 bg-dark-bg rounded-2xl border border-white/10 flex items-center justify-center z-10 shadow-xl" data-id="element-1065">
                  <Globe size={24} className="text-teal" data-id="element-1066" />
                </div>
              </div>
            </div>

            {/* Content Cards */}
            <div className="lg:col-span-5 space-y-6" data-id="element-1067">
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-xl" data-id="element-1068">
                <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center text-teal mb-6" data-id="element-1069">
                  <Zap size={24} data-id="element-1070" />
                </div>
                <h3 className="font-poppins font-bold text-xl text-white mb-3" data-id="element-1071">
                  Built in Days, Not Weeks
                </h3>
                <p className="text-steel leading-relaxed" data-id="element-1072">
                  Most agencies take 6-8 weeks and endless questionnaires to
                  build a brand voice. We do it in days because we tap directly
                  into your Zoom meetings, emails, and Slack history.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-xl relative overflow-hidden" data-id="element-1073">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple/10 rounded-full blur-2xl pointer-events-none" data-id="element-1074" />
                <div className="w-12 h-12 bg-purple/10 rounded-xl flex items-center justify-center text-purple mb-6 relative z-10" data-id="element-1075">
                  <Shield size={24} data-id="element-1076" />
                </div>
                <h3 className="font-poppins font-bold text-xl text-white mb-3 relative z-10" data-id="element-1077">
                  100% Private & Local
                </h3>
                <p className="text-steel leading-relaxed relative z-10" data-id="element-1078">
                  Your data never leaves your servers. We process everything
                  locally to build your persona. No risk of training public AI
                  models. Complete confidentiality.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. THE MARKETING WHEEL SECTION */}
      <section className="py-32 px-6 bg-white relative overflow-hidden" data-id="element-1079">
        <div className="inner-container max-w-7xl mx-auto" data-id="element-1080">
          <div className="text-center max-w-3xl mx-auto mb-24" data-id="element-1081">
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight text-gray-900 mb-6" data-id="element-1082">
              Every Piece of the Puzzle.{' '}
              <span className="text-gradient" data-id="element-1083">Handled.</span>
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1084">
              Marketing isn't just one thing—it's an ecosystem. MISTR seamlessly
              connects and executes across every discipline simultaneously.
            </p>
          </div>

          {/* Radial Wheel Graphic */}
          <div className="relative w-[320px] h-[320px] md:w-[600px] md:h-[600px] mx-auto my-12" data-id="element-1085">
            {/* Center Core */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 md:w-48 md:h-48 bg-dark-bg rounded-full border border-white/10 shadow-[0_0_60px_rgba(7,133,183,0.2)] flex flex-col items-center justify-center z-20" data-id="element-1086">
              <Brain size={40} className="text-teal mb-2" data-id="element-1087" />
              <span className="font-poppins font-bold text-white text-sm md:text-base text-center leading-tight" data-id="element-1088">
                MISTR
                <br data-id="element-1089" />
                Persona Core
              </span>
            </div>

            {/* Connecting Rings */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full border border-gray-200 rounded-full z-0" data-id="element-1090" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] border border-gray-200 rounded-full z-0" data-id="element-1091" />

            {/* The 8 Slices/Nodes */}
            {[{
            icon: Search,
            label: 'SEO',
            desc: 'Rankings & Outreach'
          }, {
            icon: TrendingUp,
            label: 'PPC',
            desc: 'Paid Ads'
          }, {
            icon: Share2,
            label: 'Social',
            desc: 'Community'
          }, {
            icon: PenTool,
            label: 'Content',
            desc: 'Blogs & Copy'
          }, {
            icon: Mail,
            label: 'Email',
            desc: 'Newsletters'
          }, {
            icon: Globe,
            label: 'Web Design',
            desc: 'UX/UI'
          }, {
            icon: Palette,
            label: 'Graphics',
            desc: 'Visuals'
          }, {
            icon: Star,
            label: 'Reputation',
            desc: 'Reviews'
          }].map((item, i) => {
            const angle = i * 360 / 8;
            // Calculate position using CSS transforms
            return <motion.div key={i} className="absolute top-1/2 left-1/2 w-20 h-20 md:w-32 md:h-32 -ml-10 -mt-10 md:-ml-16 md:-mt-16 z-10 group" style={{
              transform: `rotate(${angle}deg) translateY(-160px) rotate(-${angle}deg)`
            }}
            // Responsive adjustment for mobile vs desktop radius
            initial={{
              opacity: 0,
              scale: 0
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1,
              type: 'spring'
            }} data-id="element-1092">
                  {/* Mobile uses a smaller radius via CSS media queries in a real app, but here we use a clever trick: we scale the whole container down on mobile, so the math holds */}
                  <div className="w-full h-full bg-dark-bg border border-white/10 rounded-2xl flex flex-col items-center justify-center p-2 hover:bg-navy-dark hover:border-purple/50 transition-all cursor-default shadow-lg group-hover:shadow-[0_0_30px_rgba(124,58,237,0.3)]" data-id="element-1093">
                    <item.icon size={24} className="text-teal mb-2 group-hover:text-purple transition-colors md:w-8 md:h-8" data-id="element-1094" />
                    <span className="font-poppins font-bold text-white text-[10px] md:text-sm text-center" data-id="element-1095">
                      {item.label}
                    </span>
                    <span className="hidden md:block text-[10px] text-steel/70 text-center mt-1 leading-tight" data-id="element-1096">
                      {item.desc}
                    </span>
                  </div>
                </motion.div>;
          })}

            {/* CSS override for mobile radius scaling */}
            <style dangerouslySetInnerHTML={{
            __html: `
              @media (max-width: 768px) {
                .group { transform: rotate(var(--angle)) translateY(-130px) rotate(calc(var(--angle) * -1)) !important; }
              }
            `
          }} data-id="element-1097" />
          </div>
        </div>
      </section>

      {/* 4. DEEP DIVE SECTIONS */}

      {/* A. SEO */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1098">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1099">
          <div className="flex flex-col md:flex-row items-center gap-16" data-id="element-1100">
            <div className="flex-1" data-id="element-1101">
              <div className="w-16 h-16 bg-teal/10 rounded-2xl flex items-center justify-center text-teal mb-6" data-id="element-1102">
                <Search size={32} data-id="element-1103" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1104">
                Search Engine Dominance
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1105">
                We don't just optimize meta tags. MISTR handles the entire SEO
                ecosystem, from technical audits to natural, human-like
                relationship building.
              </p>
              <ul className="space-y-4" data-id="element-1106">
                {['On-page optimization: meta tags, structure, and keyword targeting', 'Natural backlink outreach: Not spammy software blasts. Human-like, personalized outreach that builds real relationships.', 'Technical SEO: site speed, schema markup, and crawlability', 'Continuous keyword research and content gap analysis'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1107">
                    <CheckCircle2 size={20} className="text-teal shrink-0 mt-1" data-id="element-1108" />
                    <span data-id="element-1109">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1110">
              <div className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-2xl relative" data-id="element-1111">
                <div className="absolute -top-4 -right-4 bg-purple text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg" data-id="element-1112">
                  Human-Like Outreach
                </div>
                <div className="space-y-4" data-id="element-1113">
                  <div className="h-4 w-1/3 bg-white/10 rounded animate-pulse" data-id="element-1114" />
                  <div className="h-4 w-full bg-white/5 rounded" data-id="element-1115" />
                  <div className="h-4 w-5/6 bg-white/5 rounded" data-id="element-1116" />
                  <div className="p-4 bg-dark-bg border border-white/10 rounded-lg mt-6" data-id="element-1117">
                    <p className="text-sm text-steel/70 mb-2" data-id="element-1118">
                      Drafted Email to Industry Blog:
                    </p>
                    <p className="text-sm text-white font-medium" data-id="element-1119">
                      "Hey Sarah, loved your recent piece on AI automation. I
                      noticed a small broken link in paragraph 3. Also, we just
                      published a data-backed study on this exact topic that
                      your readers might find valuable..."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* B. PPC */}
      <section className="py-24 px-6 bg-white relative overflow-hidden" data-id="element-1120">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1121">
          <div className="flex flex-col md:flex-row-reverse items-center gap-16" data-id="element-1122">
            <div className="flex-1" data-id="element-1123">
              <div className="w-16 h-16 bg-purple/10 rounded-2xl flex items-center justify-center text-purple mb-6" data-id="element-1124">
                <TrendingUp size={32} data-id="element-1125" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1126">
                Precision Paid Ads
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1127">
                Stop wasting budget on campaigns you forgot to monitor. MISTR
                watches your ad spend 24/7, optimizing for ROAS in real-time.
              </p>
              <ul className="space-y-4" data-id="element-1128">
                {['Campaign creation across Google Ads and Meta', 'Automatic A/B testing of ad copy and creatives', 'Real-time budget optimization and ROAS tracking', 'Automatic pausing of underperforming ad sets'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1129">
                    <CheckCircle2 size={20} className="text-purple shrink-0 mt-1" data-id="element-1130" />
                    <span data-id="element-1131">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1132">
              <div className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-2xl" data-id="element-1133">
                <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-4" data-id="element-1134">
                  <span className="text-white font-semibold" data-id="element-1135">
                    Campaign: Q4 Retargeting
                  </span>
                  <span className="text-teal font-bold" data-id="element-1136">+24% ROAS</span>
                </div>
                <div className="space-y-4" data-id="element-1137">
                  <div className="flex justify-between items-center bg-white/5 p-3 rounded-lg" data-id="element-1138">
                    <span className="text-steel text-sm" data-id="element-1139">
                      Ad Variant A (Paused)
                    </span>
                    <span className="text-red-400 text-sm font-medium" data-id="element-1140">
                      $45 / CPA
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-teal/10 border border-teal/30 p-3 rounded-lg" data-id="element-1141">
                    <span className="text-white text-sm font-medium" data-id="element-1142">
                      Ad Variant B (Scaling)
                    </span>
                    <span className="text-teal text-sm font-bold" data-id="element-1143">
                      $12 / CPA
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* C. Social Media */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1144">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1145">
          <div className="flex flex-col md:flex-row items-center gap-16" data-id="element-1146">
            <div className="flex-1" data-id="element-1147">
              <div className="w-16 h-16 bg-teal/10 rounded-2xl flex items-center justify-center text-teal mb-6" data-id="element-1148">
                <Share2 size={32} data-id="element-1149" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1150">
                Omnipresent Social
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1151">
                Be everywhere your audience is, without spending your life on
                social media. MISTR researches, creates, and engages in your
                exact brand voice.
              </p>
              <ul className="space-y-4" data-id="element-1152">
                {['Platform research and audience analysis', 'Content creation perfectly tuned to your brand voice', 'Automated scheduling and posting across all platforms', 'Performance tracking — doubling down on what works', 'Trend monitoring and real-time engagement'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1153">
                    <CheckCircle2 size={20} className="text-teal shrink-0 mt-1" data-id="element-1154" />
                    <span data-id="element-1155">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full grid grid-cols-2 gap-4" data-id="element-1156">
              <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex flex-col items-center justify-center text-center aspect-square shadow-xl" data-id="element-1157">
                <div className="text-3xl font-bold text-white mb-2" data-id="element-1158">14</div>
                <div className="text-sm text-steel" data-id="element-1159">Posts This Week</div>
              </div>
              <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex flex-col items-center justify-center text-center aspect-square shadow-xl" data-id="element-1160">
                <div className="text-3xl font-bold text-teal mb-2" data-id="element-1161">+42%</div>
                <div className="text-sm text-steel" data-id="element-1162">Engagement Rate</div>
              </div>
              <div className="col-span-2 bg-dark-bg border border-white/10 rounded-xl p-6 shadow-xl" data-id="element-1163">
                <div className="flex items-center gap-3 mb-3" data-id="element-1164">
                  <div className="w-8 h-8 bg-purple rounded-full flex items-center justify-center" data-id="element-1165">
                    <Star size={14} className="text-white" data-id="element-1166" />
                  </div>
                  <div data-id="element-1167">
                    <div className="text-sm font-bold text-white" data-id="element-1168">
                      Trend Detected
                    </div>
                    <div className="text-xs text-steel/70" data-id="element-1169">2 hours ago</div>
                  </div>
                </div>
                <p className="text-sm text-steel" data-id="element-1170">
                  "Drafted 3 new LinkedIn posts capitalizing on the recent
                  industry news. Ready for your approval."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* D. Content Marketing */}
      <section className="py-24 px-6 bg-white relative overflow-hidden" data-id="element-1171">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1172">
          <div className="flex flex-col md:flex-row-reverse items-center gap-16" data-id="element-1173">
            <div className="flex-1" data-id="element-1174">
              <div className="w-16 h-16 bg-purple/10 rounded-2xl flex items-center justify-center text-purple mb-6" data-id="element-1175">
                <PenTool size={32} data-id="element-1176" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1177">
                Content That Converts
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1178">
                High-quality, SEO-optimized content that actually sounds like
                you wrote it. No generic AI fluff.
              </p>
              <ul className="space-y-4" data-id="element-1179">
                {['Blog posts, landing pages, and detailed case studies', 'Written strictly adhering to your persona guidelines', 'SEO-optimized from the very first draft', 'Complete content calendar management and ideation'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1180">
                    <CheckCircle2 size={20} className="text-purple shrink-0 mt-1" data-id="element-1181" />
                    <span data-id="element-1182">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1183">
              <div className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-2xl" data-id="element-1184">
                <div className="flex items-center gap-2 mb-6" data-id="element-1185">
                  <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-1186" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-1187" />
                  <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-1188" />
                </div>
                <h4 className="text-white font-poppins font-bold text-xl mb-4" data-id="element-1189">
                  How AI is Changing B2B Sales
                </h4>
                <p className="text-steel text-sm leading-relaxed mb-4" data-id="element-1190">
                  "Most sales teams are drowning in data but starving for
                  context. We've seen this firsthand when we audited over 50
                  enterprise pipelines last quarter..."
                </p>
                <div className="flex gap-2" data-id="element-1191">
                  <span className="text-[10px] uppercase tracking-wider bg-white/10 text-white px-2 py-1 rounded" data-id="element-1192">
                    SEO Score: 94/100
                  </span>
                  <span className="text-[10px] uppercase tracking-wider bg-teal/20 text-teal px-2 py-1 rounded" data-id="element-1193">
                    Tone Match: 99%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* E. Email Marketing */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1194">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1195">
          <div className="flex flex-col md:flex-row items-center gap-16" data-id="element-1196">
            <div className="flex-1" data-id="element-1197">
              <div className="w-16 h-16 bg-teal/10 rounded-2xl flex items-center justify-center text-teal mb-6" data-id="element-1198">
                <Mail size={32} data-id="element-1199" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1200">
                Automated Email Engines
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1201">
                Nurture leads while you sleep. MISTR builds complex drip
                campaigns and newsletters that drive actual revenue.
              </p>
              <ul className="space-y-4" data-id="element-1202">
                {['Complex drip campaigns and behavioral automations', 'Weekly newsletter creation and curation', 'Deep list segmentation and personalization', 'A/B testing subject lines and body content', 'Proactive deliverability monitoring'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1203">
                    <CheckCircle2 size={20} className="text-teal shrink-0 mt-1" data-id="element-1204" />
                    <span data-id="element-1205">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1206">
              <div className="bg-dark-bg border border-white/10 rounded-2xl p-6 shadow-2xl flex flex-col gap-3" data-id="element-1207">
                <div className="bg-white/5 border border-white/10 p-4 rounded-lg flex justify-between items-center" data-id="element-1208">
                  <div data-id="element-1209">
                    <div className="text-white font-medium text-sm" data-id="element-1210">
                      Welcome Series - Day 1
                    </div>
                    <div className="text-steel/70 text-xs mt-1" data-id="element-1211">
                      Open Rate: 68%
                    </div>
                  </div>
                  <CheckCircle2 className="text-teal" size={20} data-id="element-1212" />
                </div>
                <div className="bg-white/5 border border-white/10 p-4 rounded-lg flex justify-between items-center" data-id="element-1213">
                  <div data-id="element-1214">
                    <div className="text-white font-medium text-sm" data-id="element-1215">
                      Value Add - Day 3
                    </div>
                    <div className="text-steel/70 text-xs mt-1" data-id="element-1216">
                      Open Rate: 54%
                    </div>
                  </div>
                  <CheckCircle2 className="text-teal" size={20} data-id="element-1217" />
                </div>
                <div className="bg-purple/20 border border-purple/30 p-4 rounded-lg flex justify-between items-center" data-id="element-1218">
                  <div data-id="element-1219">
                    <div className="text-white font-medium text-sm" data-id="element-1220">
                      Soft Pitch - Day 7
                    </div>
                    <div className="text-purple text-xs mt-1 font-bold" data-id="element-1221">
                      A/B Testing Subject Line...
                    </div>
                  </div>
                  <RefreshCw className="text-purple animate-spin" size={20} data-id="element-1222" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* F. Web & Graphic Design */}
      <section className="py-24 px-6 bg-white relative overflow-hidden" data-id="element-1223">
        <div className="inner-container max-w-6xl mx-auto" data-id="element-1224">
          <div className="flex flex-col md:flex-row-reverse items-center gap-16" data-id="element-1225">
            <div className="flex-1" data-id="element-1226">
              <div className="w-16 h-16 bg-purple/10 rounded-2xl flex items-center justify-center text-purple mb-6" data-id="element-1227">
                <Palette size={32} data-id="element-1228" />
              </div>
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-1229">
                Visual Identity & Web
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1230">
                Your brand needs to look as good as it sounds. MISTR handles the
                visual heavy lifting across all your channels.
              </p>
              <ul className="space-y-4" data-id="element-1231">
                {['Self-building, continuously optimizing website', 'Branded social graphics and carousel creation', 'High-converting ad creatives and variations', 'Consistent visual identity across every touchpoint'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1232">
                    <CheckCircle2 size={20} className="text-purple shrink-0 mt-1" data-id="element-1233" />
                    <span data-id="element-1234">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-1235">
              <div className="grid grid-cols-2 gap-4" data-id="element-1236">
                <div className="bg-dark-bg border border-white/10 rounded-2xl p-6 aspect-square flex flex-col items-center justify-center text-center shadow-xl" data-id="element-1237">
                  <Globe className="text-teal mb-4" size={40} data-id="element-1238" />
                  <span className="text-white font-poppins font-semibold" data-id="element-1239">
                    Web Updates
                  </span>
                  <span className="text-steel text-xs mt-2" data-id="element-1240">
                    Deployed 2 hrs ago
                  </span>
                </div>
                <div className="bg-dark-bg border border-white/10 rounded-2xl p-6 aspect-square flex flex-col items-center justify-center text-center shadow-xl" data-id="element-1241">
                  <Layers className="text-purple mb-4" size={40} data-id="element-1242" />
                  <span className="text-white font-poppins font-semibold" data-id="element-1243">
                    Ad Creatives
                  </span>
                  <span className="text-steel text-xs mt-2" data-id="element-1244">
                    12 variants generated
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. STRATEGY SECTION */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1245">
        <div className="inner-container max-w-7xl mx-auto" data-id="element-1246">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1247">
            <span className="text-teal text-sm font-poppins font-bold uppercase tracking-widest block mb-4" data-id="element-1248">
              The Masterplan
            </span>
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight text-gray-900 mb-6" data-id="element-1249">
              Real Strategies. <br className="hidden md:block" data-id="element-1250" /> Not Just
              Automation.
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1251">
              Automation without strategy is just making mistakes faster. MISTR
              executes proven, high-level marketing playbooks.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-1252">
            {[{
            icon: RefreshCw,
            title: 'The Content Flywheel',
            desc: 'MISTR writes a core blog post, breaks it into 5 social posts, drafts an email newsletter about it, and creates retargeting ads for readers. One idea, infinite mileage.',
            color: 'text-teal',
            bg: 'bg-teal/10'
          }, {
            icon: Globe,
            title: 'The Omnipresence Play',
            desc: 'Be everywhere your customer looks. MISTR ensures your messaging is perfectly synced across Search, Social, Email, and your Website simultaneously.',
            color: 'text-purple',
            bg: 'bg-purple/10'
          }, {
            icon: Target,
            title: 'The Trust Ladder',
            desc: 'Cold traffic needs different messaging than warm leads. MISTR automatically moves prospects from Awareness → Value → Authority → Conversion.',
            color: 'text-teal',
            bg: 'bg-teal/10'
          }, {
            icon: Database,
            title: 'The Data Loop',
            desc: 'Every click, open, and conversion feeds back into the Persona Engine. MISTR learns what works and automatically adjusts future campaigns to match.',
            color: 'text-purple',
            bg: 'bg-purple/10'
          }].map((strategy, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-dark-bg border border-white/10 rounded-2xl p-8 shadow-xl hover:shadow-2xl hover:border-purple/30 transition-all group" data-id="element-1253">
                <div className={`w-14 h-14 ${strategy.bg} rounded-xl flex items-center justify-center ${strategy.color} mb-6 group-hover:scale-110 transition-transform`} data-id="element-1254">
                  <strategy.icon size={28} data-id="element-1255" />
                </div>
                <h3 className="font-poppins font-bold text-2xl text-white mb-3" data-id="element-1256">
                  {strategy.title}
                </h3>
                <p className="text-steel leading-relaxed" data-id="element-1257">{strategy.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. SELF-EVOLVING PERSONA */}
      <section className="py-24 px-6 bg-white relative overflow-hidden border-t border-gray-100" data-id="element-1258">
        <div className="inner-container max-w-5xl mx-auto" data-id="element-1259">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-1260">
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight text-gray-900 mb-6" data-id="element-1261">
              A Persona That Grows With You
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1262">
              Static brand guides collect dust. When your business pivots,
              launches a new product, or shifts tone, MISTR adapts
              automatically. No manual updates required.
            </p>
          </div>

          <div className="relative" data-id="element-1263">
            {/* Timeline Line */}
            <div className="absolute left-[27px] md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-purple via-teal to-purple md:-translate-x-1/2 opacity-50" data-id="element-1264" />

            <div className="space-y-12" data-id="element-1265">
              {[{
              time: 'Day 1',
              title: 'The Foundation',
              desc: 'MISTR ingests your historical data (Zoom, Slack, Emails) to establish your baseline voice, core offers, and target audience.',
              align: 'right'
            }, {
              time: 'Month 1',
              title: 'The Refinement',
              desc: 'By analyzing which social posts and emails get the highest engagement, MISTR subtly tweaks its tone to maximize resonance.',
              align: 'left'
            }, {
              time: 'Month 6',
              title: 'The Pivot',
              desc: 'You launch a new service. MISTR automatically reads the new sales collateral and instantly updates all marketing channels to reflect the new offering.',
              align: 'right'
            }, {
              time: 'Year 1',
              title: 'The Mastermind',
              desc: 'MISTR now knows your customers better than anyone. It anticipates market trends and suggests new marketing angles based on a year of deep data.',
              align: 'left'
            }].map((step, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className={`relative flex flex-col md:flex-row items-start md:items-center gap-8 ${step.align === 'left' ? 'md:flex-row-reverse' : ''}`} data-id="element-1266">
                  {/* Timeline Dot */}
                  <div className="absolute left-0 md:left-1/2 w-14 h-14 bg-dark-bg border-4 border-purple rounded-full flex items-center justify-center md:-translate-x-1/2 z-10 shadow-[0_0_20px_rgba(124,58,237,0.4)]" data-id="element-1267">
                    <Clock size={20} className="text-white" data-id="element-1268" />
                  </div>

                  <div className={`ml-20 md:ml-0 md:w-1/2 ${step.align === 'left' ? 'md:pr-16 md:text-right' : 'md:pl-16'}`} data-id="element-1269">
                    <div className="text-teal font-bold text-sm uppercase tracking-wider mb-2" data-id="element-1270">
                      {step.time}
                    </div>
                    <h3 className="font-poppins font-bold text-2xl text-gray-900 mb-3" data-id="element-1271">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-1272">{step.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 7. FINAL CTA */}
      <section className="relative py-32 px-6 bg-hero-gradient overflow-hidden flex items-center justify-center text-center border-t border-white/10" data-id="element-1273">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1274">
          <motion.div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple/30 rounded-full blur-[100px]" animate={{
          x: [0, 50, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1275" />
          <motion.div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal/20 rounded-full blur-[100px]" animate={{
          x: [0, -40, 0],
          y: [0, -50, 0]
        }} transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1276" />
        </div>

        <div className="inner-container relative z-10 max-w-4xl" data-id="element-1277">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1278">
            <h2 className="font-poppins font-extrabold text-5xl md:text-6xl mb-6 leading-tight text-white" data-id="element-1279">
              Put Your Marketing <br data-id="element-1280" />
              <span className="text-gradient" data-id="element-1281">On Autopilot.</span>
            </h2>
            <p className="text-xl text-steel mb-10" data-id="element-1282">
              One AI. Every channel. Your exact voice. Always on.
            </p>

            <button className="btn-primary text-lg px-10 py-5 mb-12 shadow-[0_0_40px_rgba(124,58,237,0.4)] hover:shadow-[0_0_60px_rgba(124,58,237,0.6)] transition-shadow flex items-center gap-2 mx-auto" data-id="element-1283">
              Book a Strategy Call <ArrowRight size={20} data-id="element-1284" />
            </button>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm" data-id="element-1285">
              <div data-id="element-1286">
                <div className="text-2xl font-bold text-white font-poppins mb-1" data-id="element-1287">
                  100%
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1288">
                  Automated
                </div>
              </div>
              <div data-id="element-1289">
                <div className="text-2xl font-bold text-white font-poppins mb-1" data-id="element-1290">
                  Local
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1291">
                  Data Privacy
                </div>
              </div>
              <div data-id="element-1292">
                <div className="text-2xl font-bold text-white font-poppins mb-1" data-id="element-1293">
                  8+
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1294">
                  Channels
                </div>
              </div>
              <div data-id="element-1295">
                <div className="text-2xl font-bold text-white font-poppins mb-1" data-id="element-1296">
                  24/7
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-1297">
                  Execution
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>;
}